/**
 * The script an automation record effectively carries.
 *
 * Lives here rather than in draft-script-section.tsx because both that
 * component and RecordBody's tab resolution need it, and exporting a
 * function from a component file breaks Vite fast refresh.
 */

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

/** `{source, fixtures}` as stored in `ahoy_script`. */
interface ScriptValue {
  source?: unknown;
}

/**
 * An `import x` / `from x import y` statement (the only thing
 * `cleanScriptForDisplay` strips). Deliberately NOT global — a `/g` regex
 * carries `lastIndex` between `.test()` calls and would skip every other line.
 */
const IMPORT_STATEMENT = /^\s*(import\s|from\s+[\w.]+\s+import\s)/;

/**
 * Advance the multiline-string state across one line, returning the triple-quote
 * delimiter (`"""` or `'''`) still open at the line's end, or null.
 *
 * A naive `"""|'''` scan toggles on any triple-quote-looking run — including one
 * inside a `#` comment (`# see """docs"""`) or an ordinary single-line string
 * (`x = "a ''' b"`), where three quotes are data, not a delimiter. Left odd, that
 * flips the scanner into a phantom "inside a docstring" state and the imports on
 * the lines that follow are wrongly kept in the displayed source. So this lexes
 * the line: while a triple string is open only its matching triple closes it;
 * otherwise a `#` ends the line as a comment, single-line strings are consumed
 * whole, and only a real triple-quote token toggles state.
 */
function scanMultilineStringState(
  line: string,
  openString: string | null
): string | null {
  // The single-line quote (`'` or `"`) currently open on THIS line, or null.
  // Single-line strings never span lines, so this never carries across calls.
  let single: string | null = null;
  let i = 0;
  while (i < line.length) {
    if (openString !== null) {
      if (line[i] === '\\') {
        // A backslash escapes the next character, so an escaped delimiter
        // (`\"""`) is data, not a close — skip both to avoid ending the
        // string early and wrongly exposing the import-looking lines after it.
        i += 2;
      } else if (line.startsWith(openString, i)) {
        openString = null;
        i += 3;
      } else {
        i += 1;
      }
      continue;
    }
    const char = line[i];
    if (single !== null) {
      if (char === '\\') {
        i += 2; // skip an escaped character (e.g. an escaped quote)
      } else if (char === single) {
        single = null;
        i += 1;
      } else {
        i += 1;
      }
      continue;
    }
    if (char === '#') {
      break; // a comment runs to the end of the line
    }
    if (line.startsWith('"""', i)) {
      openString = '"""';
      i += 3;
    } else if (line.startsWith("'''", i)) {
      openString = "'''";
      i += 3;
    } else if (char === '"' || char === "'") {
      single = char;
      i += 1;
    } else {
      i += 1;
    }
  }
  return openString;
}

/**
 * Drop a trailing `#` comment before counting parentheses. A multi-line import
 * may carry a comment whose own parentheses would otherwise be counted as part
 * of the import block, ending the block early or leaking its tail into the
 * displayed code. An import statement never contains a literal `#` outside a
 * comment, so cutting at the first `#` is safe here — and this feeds the
 * paren-count only; the lines shown to the user are never modified.
 */
function importParenDelta(line: string): number {
  const hashIndex = line.indexOf('#');
  const counted = hashIndex === -1 ? line : line.slice(0, hashIndex);
  const opens = (counted.match(/\(/g) ?? []).length;
  const closes = (counted.match(/\)/g) ?? []).length;
  return opens - closes;
}

/**
 * The record's `ahoy_script` source, trimmed. A pending draft's proposed
 * script arrives the same way — projected onto the object — so there is one
 * read for both. Empty string when there is none, which is how the Code tab
 * knows not to exist at all.
 */
export function getEffectiveScriptSource(
  object: CrmObject | undefined
): string {
  const source = getPropertyValue<ScriptValue>(
    object?.properties?.ahoy_script
  )?.source;
  return typeof source === 'string' ? source.trim() : '';
}

/**
 * The script as the USER should read it: everything except the imports.
 *
 * The import block is the only genuinely repeated part of an agent-authored
 * script — it is the same allowlisted sandbox surface every time — so it says
 * nothing about what THIS automation does and only pushes the interesting
 * lines down. Everything else is kept.
 *
 * The `def run(...)` signature is KEPT deliberately. It is the most
 * information-dense line in the file: it declares the trigger the automation
 * reacts to (`event: AssociationAddedTrigger[Deal, DealPipelineStage]`) and
 * the output events it can emit (`-> list[DealUpdatedResult]`) — exactly what
 * a reviewer wants to know first. Private helpers are kept for the same
 * reason: a script that calls `_size_tag(...)` reads as nonsense if the helper
 * is invisible.
 *
 * Mechanically this drops only `import …` / `from … import …` statements,
 * including parenthesised multi-line forms, then trims the blank lines they
 * leave behind. Import-looking lines INSIDE a docstring/multiline string are
 * kept (they are documentation, not real imports). Top-level structure and
 * indentation are otherwise untouched, so nothing needs dedenting and there is
 * no parse that can fail.
 *
 * Display-only. Capability checks (does a Code tab exist?) use the raw source.
 */
export function cleanScriptForDisplay(source: string): string {
  const lines = source.split('\n');
  const kept: string[] = [];
  // Tracks an unclosed `from x import (` so its continuation lines drop too.
  // Counting parens rather than looking for a lone `)` keeps a parenthesised
  // alias inside the block from ending it early.
  let openParens = 0;
  // Delimiter of the currently-open triple-quoted string (`"""` or `'''`), or
  // null. A line whose start falls inside a docstring/multiline string is
  // content — a `from … import` example inside it must never be stripped.
  let openString: string | null = null;
  // True when the previous line was a dropped import (or its continuation), so
  // a blank line left directly behind the removed import block is collapsed
  // rather than left to push the body down (`.trim()` only clears the ends).
  let lastLineWasDroppedImport = false;

  for (const line of lines) {
    const startedInsideString = openString !== null;
    openString = scanMultilineStringState(line, openString);

    if (startedInsideString) {
      kept.push(line);
      lastLineWasDroppedImport = false;
      continue;
    }
    if (openParens > 0) {
      openParens += importParenDelta(line);
      lastLineWasDroppedImport = true;
      continue;
    }
    if (IMPORT_STATEMENT.test(line)) {
      openParens = importParenDelta(line);
      lastLineWasDroppedImport = true;
      continue;
    }
    if (lastLineWasDroppedImport && line.trim() === '') {
      continue;
    }
    lastLineWasDroppedImport = false;
    kept.push(line);
  }

  return kept.join('\n').trim();
}
